import { uuidv7 } from './uuidv7'
import { payloadHash } from './payloadHash'
import { appendLocalEvent } from './db'
import { drainOutbox } from './sync'
import { stableDeviceId } from './device'
import { firePush } from './push'
import { pushSummaryForEvent } from './pushSummary'
import type { EventType, JSONValue } from './types'

interface EmitParams {
  familyId: string
  personId: string | null
  userId: string
  eventType: EventType
  payload: JSONValue
  occurredAt?: Date
  supersedes?: string | null
  schemaVersion?: number
}

/**
 * Emit an event — offline-first canonical write path.
 * 1. Compute UUIDv7 idempotency key + payload hash
 * 2. Append to local IndexedDB (idempotent)
 * 3. Enqueue in outbox
 * 4. Kick off drain (fire-and-forget)
 *
 * The event will eventually sync via insert_event() RPC. If offline, it stays in outbox
 * with exponential backoff until reconnect.
 */
export async function emitEvent(params: EmitParams): Promise<string> {
  const id = uuidv7()
  const now = new Date()
  const occurred = params.occurredAt ?? now
  const deviceId = await stableDeviceId()
  const hash = await payloadHash(params.payload)

  await appendLocalEvent({
    id,
    family_id: params.familyId,
    person_id: params.personId,
    user_id: params.userId,
    device_id: deviceId,
    writer_role: 'user',
    event_type: params.eventType,
    payload: params.payload,
    payload_hash: hash,
    schema_version: params.schemaVersion ?? 1,
    occurred_at: occurred.toISOString(),
    recorded_at: now.toISOString(),
    supersedes_event_id: params.supersedes ?? null,
    source: 'app',
  })

  // Kick off drain but don't await — UI stays responsive
  void drainOutbox().catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[BabyOS] drainOutbox failed', err)
  })

  // Fan out a push notification to OTHER family members. Skipped for corrections
  // and person.* events (too noisy). Fire-and-forget; UI never waits on push.
  const notifiable = pushSummaryForEvent(params.eventType, params.payload)
  if (notifiable) {
    void firePush({
      familyId: params.familyId,
      excludeUserId: params.userId,
      title: notifiable.title,
      body: notifiable.body,
      url: notifiable.url ?? '/timeline',
      tag: notifiable.tag ?? 'partner-event',
      requirePref: 'partner_events',
    })
  }

  // HS17: no automatic celebration/email invocation. Push above remains separate.

  return id
}
